import { createSignal, For, Show, untrack } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  COLOR_TRANSITION,
  FOCUS_RING,
} from "@/domains/freelance/taxonomy/components/sector-selector/primitives";
import {
  categoryLabel,
  categoryOptions,
} from "@/domains/freelance/taxonomy/store/taxonomy-store";
import { cn } from "@/libs/cn";
import {
  chooseCategory,
  chooseWholeCategory,
  type DraftOutcome,
  type DraftStep,
  headerCount,
  resetDraft,
  type SubcategoryDraft,
  toggleSubcategory,
} from "../../utils/subcategory-filter";
import { CategoryPanel } from "./category-panel";

const I18N = "company.search.freelancers.result.filters.job_subcategory";

const HEADER_CLASS = cn(
  "flex min-h-9 w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-foreground hover:bg-accent",
  COLOR_TRANSITION,
  FOCUS_RING,
);

/** The other categories, when one is chosen: at 60% the text stays above 4.5:1. */
const FADED_CLASS = "opacity-60 hover:opacity-100 focus-visible:opacity-100";

export type CategoryAccordionProps = {
  surface: "search" | "pool";
  /** Controlled: the popover draft. */
  draft: SubcategoryDraft;
  onDraft: (next: SubcategoryDraft, outcome: DraftOutcome | null) => void;
  /** «Fatto» and Esc call it; the popover closes and returns focus to the trigger. */
  onClose: () => void;
  /** The popover calls `focusInitial` from its `onOpenAutoFocus`. */
  onReady?: (api: { focusInitial: () => void }) => void;
};

/**
 * The body of the «Categoria e ambiti» popover: the 8 categories as an
 * accordion, at most one open. Expanding a category chooses it (another one
 * drops the subcategories); collapsing it leaves it chosen. The other
 * categories fade but stay clickable. The sticky footer holds the status row
 * (the rule, or the outcome of what a step removed), «Fatto» and «Azzera».
 *
 * It mounts fresh each time the popover opens, so the outcome goes back to the
 * rule on reopen.
 *
 * Motion: the Kobalte primitive without the animated height of the house
 * accordion, so a category opens instantly; the chevron turns without a
 * transition; the open category scrolls into view without animation. Colors
 * at 150 ms; fading and coming back are instant.
 */
export const CategoryAccordion = (props: CategoryAccordionProps) => {
  const { t } = useI18n();
  const [expanded, setExpanded] = createSignal<string[]>(
    untrack(() => (props.draft.category ? [props.draft.category] : [])),
  );
  /** What a category change or «Azzera» just removed; `null` shows the rule. */
  const [outcome, setOutcome] = createSignal<DraftOutcome | null>(null);
  let body: HTMLDivElement | undefined;

  const isChosen = (category: string) => props.draft.category === category;
  const isFaded = (category: string) =>
    !!props.draft.category && !isChosen(category);

  const itemOf = (category: string) =>
    [...(body?.querySelectorAll<HTMLElement>("[data-item]") ?? [])].find(
      (el) => el.dataset.item === category,
    );

  const apply = (step: DraftStep) => {
    setOutcome(step.outcome);
    props.onDraft(step.next, step.outcome);
  };

  /** On open the focus goes to the chosen category, or to the first one. */
  const focusInitial = () =>
    untrack(() => {
      const target =
        body?.querySelector<HTMLElement>("[data-chosen]") ??
        body?.querySelector<HTMLElement>("button");
      target?.focus();
      const category = props.draft.category;
      if (category) itemOf(category)?.scrollIntoView({ block: "nearest" });
    });

  props.onReady?.({ focusInitial });

  /** Expanding a category chooses it; collapsing it leaves it chosen. */
  const onExpand = (keys: string[]) => {
    setExpanded(keys);
    const key = keys[0];
    if (!key) return;
    apply(chooseCategory(props.draft, key));
    queueMicrotask(() => itemOf(key)?.scrollIntoView({ block: "nearest" }));
  };

  /**
   * Kobalte's accordion keeps Esc to itself (`preventDefault`, and collapses
   * the open category), so the popover would never see it. Here Esc closes
   * the popover. On the accordion root it runs before Kobalte's handler; on
   * the wrapper it covers «Fatto» and «Azzera», outside the root.
   */
  const closeOnEscape = (event: KeyboardEvent) => {
    if (event.key !== "Escape" || event.defaultPrevented) return;
    event.preventDefault();
    props.onClose();
  };

  /** «Azzera»: category and subcategories go; the popover stays open. */
  const reset = () => {
    const previous = props.draft.category;
    apply(resetDraft(props.draft));
    setExpanded([]);
    // «Azzera» becomes disabled under the focus: it goes to the category removed.
    queueMicrotask(() =>
      itemOf(previous)?.querySelector<HTMLElement>("button")?.focus(),
    );
  };

  const outcomeText = () => {
    const current = outcome();
    if (!current) return null;
    if (current.kind === "removed") {
      const category = categoryLabel(current.category);
      return current.count === 1
        ? t(`${I18N}.outcome_removed_one`, { category })
        : t(`${I18N}.outcome_removed`, { count: current.count, category });
    }
    return current.hadSubcategories
      ? t(`${I18N}.outcome_reset_with_subcategories`)
      : t(`${I18N}.outcome_reset`);
  };

  const rule = () =>
    props.draft.category
      ? t(`${I18N}.rule_with_category`)
      : t(`${I18N}.rule_no_category`);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: catches Esc bubbling from «Fatto» and «Azzera», outside the accordion root; the controls inside own the interaction
    <div class="contents" onKeyDown={closeOnEscape}>
      <div ref={body} class="flex flex-col p-1.5">
        <Accordion
          value={expanded()}
          onChange={onExpand}
          onKeyDown={closeOnEscape}
          collapsible
          class="flex flex-col"
        >
          <For each={categoryOptions()}>
            {(option) => (
              <Accordion.Item value={option.value} data-item={option.value}>
                <Accordion.Header class="flex">
                  <Accordion.Trigger
                    class={cn(
                      HEADER_CLASS,
                      isFaded(option.value) && FADED_CLASS,
                    )}
                    data-chosen={isChosen(option.value) ? "" : undefined}
                  >
                    <span
                      class="min-w-0 flex-1"
                      classList={{ "font-semibold": isChosen(option.value) }}
                    >
                      {option.label}
                    </span>
                    <Show when={headerCount(props.draft, option.value)}>
                      {(count) => (
                        <>
                          <span class="sr-only">
                            {`, ${
                              props.draft.subcategories.length > 0
                                ? t(`${I18N}.sr_selected`, { count: count() })
                                : t(`${I18N}.sr_all`, { count: count() })
                            }`}
                          </span>
                          <span
                            class="rounded-full bg-ring px-1.5 text-[11px] font-semibold leading-5 tabular-nums text-white"
                            aria-hidden="true"
                          >
                            {count()}
                          </span>
                        </>
                      )}
                    </Show>
                    <i
                      class="ph ph-caret-down shrink-0"
                      classList={{
                        "rotate-180": expanded().includes(option.value),
                        // With the row faded, the chevron already at 60% would drop below 3:1.
                        "text-foreground": isFaded(option.value),
                        "text-foreground/60": !isFaded(option.value),
                      }}
                      aria-hidden="true"
                    />
                  </Accordion.Trigger>
                </Accordion.Header>
                <Accordion.Content class="pb-1.5 pl-3">
                  <CategoryPanel
                    surface={props.surface}
                    draft={props.draft}
                    category={option.value}
                    onWhole={() => apply(chooseWholeCategory(props.draft))}
                    onToggle={(key) =>
                      apply(toggleSubcategory(props.draft, key))
                    }
                  />
                </Accordion.Content>
              </Accordion.Item>
            )}
          </For>
        </Accordion>
      </div>
      <div class="sticky bottom-0 flex flex-col gap-1.5 border-t border-gray-200 bg-popover p-1.5 [@media(max-height:31.25em)]:static">
        {/* The outcome takes the place of the rule; the region below reads it. */}
        <p
          class="px-1 text-xs text-foreground/70"
          aria-hidden={outcomeText() ? "true" : undefined}
        >
          {outcomeText() ?? rule()}
        </p>
        {/* Always mounted, and only with the outcome: the rule is never announced. */}
        <p role="status" class="sr-only">
          {outcomeText()}
        </p>
        <Button
          type="button"
          size="sm"
          class="w-full"
          onClick={() => props.onClose()}
        >
          {t(`${I18N}.done`)}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          class="w-full"
          disabled={!props.draft.category}
          onClick={reset}
        >
          {t(`${I18N}.reset`)}
        </Button>
      </div>
    </div>
  );
};
